import type { PlanImportProblem } from '../../../../api/organization/allocationWindowTypes';

/**
 * The plan import's file handling (W2-3): reading the sheet, the template, and
 * which text a problem gets. The rows themselves are read by the backend
 * (`new-backend/admin/src/lib/organizations/planImport.ts`), so nothing here
 * decides what a row means.
 */

/**
 * The backend reads at most 100 kB of JSON body (`express.json()` default) and
 * 1,000 rows. A file is checked twice: its size before reading, and the body
 * it becomes (JSON escaping and UTF-8 can make a Windows-1252 file larger),
 * so a too-large plan gets this message rather than the server's 413.
 */
export const MAX_PLAN_FILE_BYTES = 90 * 1024;
const MAX_PLAN_BODY_BYTES = 95 * 1024;

/** UTF-8 bytes of a string, without a browser API (so the same in every test environment). */
const utf8Length = (text: string): number => {
    let bytes = 0;
    for (const char of text) {
        const code = char.codePointAt(0) ?? 0;
        if (code < 0x80) bytes += 1;
        else if (code < 0x800) bytes += 2;
        else if (code < 0x10000) bytes += 3;
        else bytes += 4;
    }
    return bytes;
};

export const planBodyTooLarge = (csv: string): boolean =>
    utf8Length(JSON.stringify({ csv, season: { from: '0000-00-00', to: '0000-00-00' } })) > MAX_PLAN_BODY_BYTES;

/**
 * The template: the headers the import reads, and one row with invented names
 * so the shape is clear. Semicolons, because German Excel opens those as columns.
 */
export const planTemplateCsv = (headers: string[], example: string[]): string =>
    `﻿${headers.join(';')}\r\n${example.join(';')}\r\n`;

/** The codes a row or a club × room pair can carry; anything else shows the server's message. */
const KNOWN_PROBLEMS = new Set([
    'missing_value',
    'unknown_room',
    'ambiguous_room',
    'unknown_organization',
    'ambiguous_organization',
    'invalid_value',
    'invalid_window',
    'room_dedicated',
    'no_running_contract',
    'contract_ambiguous',
    'window_overlap',
    'window_overlap_own',
    'window_invalid',
]);

/** The translation key for a problem, or null to show the server's message. */
export const planProblemKey = (problem: PlanImportProblem): string | null =>
    KNOWN_PROBLEMS.has(problem.code) ? `allocation_windows.import.problem.${problem.code}` : null;

const FIELDS = new Set(['room', 'organization', 'weekday', 'startTime', 'endTime', 'validFrom', 'validTo']);

/** For missing_value / invalid_value the detail names a column; translated, else as sent. */
export const planFieldKey = (detail: string | undefined): string | null =>
    detail && FIELDS.has(detail) ? `allocation_windows.import.field.${detail}` : null;

const REFUSALS = new Set(['empty', 'missing_columns', 'no_rows', 'too_many_rows', 'season_invalid']);

/** A file the backend refused outright (400 `import_invalid`): its reason's key, or null. */
export const planRefusalKey = (error: unknown): string | null => {
    const reason = (error as { data?: { reason?: unknown } } | undefined)?.data?.reason;
    return typeof reason === 'string' && REFUSALS.has(reason) ? `allocation_windows.import.refused.${reason}` : null;
};
